import type { LoginSuccessResponse, RefreshTokenRequest, UserLoginRequest } from '@/types'
import { AppRoute } from '@/constants'
import { authHttp } from '@/http'
import { fetchLogout, fetchRefreshToken } from '@/modules/authentication/authApi'
import { useAuth } from '@/modules/authentication/authStore'
import router from '@/router'
import { destroySensitiveInfo, getRefreshToken, saveToken } from '@/services/localStorage'
import { decodeJwtToken } from '@/utils/token'

export async function loginWithCredential({ login, password }: UserLoginRequest): Promise<{ data: LoginSuccessResponse | undefined, errorMessage: string }> {
  const data: UserLoginRequest = { login: login.trim(), password: password.trim() }
  const response = ref<LoginSuccessResponse | undefined>(undefined)
  const errorMessage = ref('')
  const authStore = useAuth()

  try {
    response.value = await authHttp.post<LoginSuccessResponse>('/Auth/login', data)
    await authStore.authLoaded
  }
  catch (error: any) {
    if (error.response && error.response.data && error.response.data.message) {
      errorMessage.value = error.response.data.message
    }
    else {
      errorMessage.value = 'Логин или пароль неверный. Проверьте правильность ввода и повторите попытку.'
    }
    return { data: undefined, errorMessage: errorMessage.value }
  }

  if (!response.value) return { data: undefined, errorMessage: errorMessage.value }
  const { token, refreshToken } = response.value
  if (token && refreshToken) {
    const tokenData = decodeJwtToken(token)
    saveToken(token, refreshToken, tokenData.exp)
  }

  return { data: response.value, errorMessage: errorMessage.value }
}

export async function refreshToken(): Promise<string | undefined> {
  const currentRefreshToken = getRefreshToken()
  if (!currentRefreshToken) {
    await handleAuthFailure()
    return
  }

  try {
    const data: RefreshTokenRequest = { refreshToken: currentRefreshToken }
    const res = await fetchRefreshToken(data)

    if (res?.token && res.refreshToken) {
      const tokenData = decodeJwtToken(res.token)
      saveToken(res.token, res.refreshToken, tokenData.exp)
      return 'Successfully'
    }
  }
  catch (error) {
    console.error('Token refresh failed:', error)
  }

  await handleAuthFailure()
}

export async function logout(): Promise<void> {
  const currentRefreshToken = getRefreshToken()
  if (currentRefreshToken) {
    const data: RefreshTokenRequest = { refreshToken: currentRefreshToken }
    await fetchLogout(data)
  }
  await handleAuthFailure()
}

async function handleAuthFailure(): Promise<void> {
  destroySensitiveInfo()
  await router.push({ name: AppRoute.Login.name })
}
